import React from 'react';
import { portfolioData } from '../mock';
import {
  Mail, Linkedin, Github, BookOpen, ArrowRight,
  ExternalLink, Code2, Database, Cloud, Cpu, Brain, BarChart3, Eye,
  Building, Users, TrendingUp, FileText, FileCode, Image as ImageIcon, Link as LinkIcon
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import AboutMe from './AboutMe';

const Portfolio = () => {
  const [preview, setPreview] = React.useState(null);
  const iconMap = {
    Mail, Linkedin, Github, BookOpen
  };
  const resolveAssetUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//.test(url)) return url;
    const rawPath = url.startsWith('/') ? url : `/projects/${url}`;
    const normalized = rawPath.replace(/\\/g, '/');
    const encoded = normalized
      .split('/')
      .map((seg, idx) => (idx === 0 && seg === '' ? '' : encodeURIComponent(seg)))
      .join('/');
    const base = (process.env.PUBLIC_URL || '') + encoded;
    return (typeof window !== 'undefined') ? new URL(base, window.location.origin).toString() : base;
  };


  const techCategoryIcons = {
    aiMl: Brain,
    dataScience: BarChart3,
    backend: Database,
    frontend: Code2,
    cloud: Cloud,
    tools: Cpu
  };

  const deviconMap = {
    Python: 'python',
    R: 'r',
    Pandas: 'pandas',
    NumPy: 'numpy',
    'Scikit-learn': 'scikitlearn',
    Jupyter: 'jupyter',
    Plotly: 'plotly',
    Matplotlib: 'matplotlib',
    'PostgreSQL': 'postgresql',
    MongoDB: 'mongodb',
    MySQL: 'mysql',
    Redis: 'redis',
    Docker: 'docker',
    'Node.js': 'nodejs',
    React: 'react',
    NextJS: 'nextjs',
    TypeScript: 'typescript',
    JavaScript: 'javascript',
    HTML5: 'html5',
    CSS3: 'css3',
    'Tailwind CSS': 'tailwindcss',
    AWS: 'cloud',
    Kubernetes: 'kubernetes',
    Hadoop: 'hadoop',
    Spark: 'apachespark',
    Elasticsearch: 'elasticsearch',
    Linux: 'linux',
    Git: 'git',
    GitHub: 'github',
    Trello: 'trello',
    'Azure DevOps': 'azuredevops',
    Postman: 'postman',
    TensorFlow: 'tensorflow',
    PyTorch: 'pytorch',
    FastAPI: 'fastapi',
    Flask: 'flask',
    HTML: 'html5',
    CSS: 'css3',
  };

  const localIconMap = {
    'Data Analysis': 'graph.svg',
    'Statistical Modeling': 'sigma.svg',
    MCP: 'mcp.png',
    'AI Agents': 'robot.svg',
    CrewAI: 'crewai.png',
    LangGraph: 'graph.svg',
    RAG: 'layers.svg',
    'OpenAI API': 'brain.svg',
    'Computer Vision': 'eye.svg',
    NLP: 'text.svg',
    BERT: 'google.svg',
    'Neural Networks': 'brain.svg',
    Boosting: 'bolt.svg',
    Microservices: 'microservices.svg',
    'Responsive Design': 'responsive.svg',
    S: 'code.svg',
    'CI/CD': 'pipeline.svg',
    DevOps: 'devops.svg',
    APIs: 'api.svg',
    Analytics: 'graph.svg',
    GA4: 'google-analytics.svg',
    AWS: 'aws.png',
  };

  const brandIconMap = {
    AWS: 'amazon',
    Analytics: 'googleanalytics',
    GA4: 'googleanalytics',
    BERT: 'google',
  };

  const getTechIconUrl = (name) => {
    const local = localIconMap[name];
    if (local) return process.env.PUBLIC_URL + '/icons/' + local;
    const slug = deviconMap[name];
    if (slug) {
      return `https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${slug}/${slug}-original.svg`;
    }
    const brand = brandIconMap[name];
    if (brand) {
      return `https://cdn.simpleicons.org/${brand}`;
    }
    return null;
  };

  const getExpBannerStyle = (company) => {
    const map = {
      'We Are Clickers': 'rgb(12, 12, 12)',
      'AIEDUTECH': 'rgb(40, 123, 152)',
      'Gaia Consulting': 'rgb(155, 210, 204)'
    };
    const bg = map[company];
    return bg ? { background: bg } : undefined;
  };

  const getExpCompanyStyle = (company) => {
    return { color: '#ffffff' };
  };

  const getExpBannerLayerStyle = (company) => {
    const map = {
      'We Are Clickers': 'rgb(12, 12, 12)',
      'AIEDUTECH': 'rgb(40, 123, 152)',
      'Gaia Consulting': 'rgb(155, 210, 204)'
    };
    const color = map[company] || '#e2e8f0';
    // Soft radial gradient using the company color
    return {
      background: `radial-gradient(120% 120% at 50% 0%, ${color} 0%, ${color} 36%, rgba(255,255,255,0.65) 72%, #ffffff 100%)`
    };
  };

  const getExpLogoUrl = (company) => {
    const map = {
      'We Are Clickers': '/experience/weareclickersLogo.png',
      'AIEDUTECH': '/experience/aiedutechLogo.jpg',
      'Gaia Consulting': '/experience/gaiaLogo.png',
    };
    const rel = map[company];
    return rel ? (process.env.PUBLIC_URL + rel) : null;
  };

  // JSON-LD structured data
  const personJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Jon Ortega Uzquiaga',
    alternateName: 'Jon Ortega',
    jobTitle: 'Data & AI Engineer',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Bilbao',
      addressRegion: 'Euskadi',
      addressCountry: 'ES'
    },
    url: typeof window !== 'undefined' ? window.location.origin : 'https://jon.ortega',
    sameAs: (portfolioData.socialLinks || []).map(l => l.url)
  };
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Wrappers AI',
    url: 'https://wrappers.es',
    founder: 'Jon Ortega Uzquiaga',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Bilbao',
      addressRegion: 'Euskadi',
      addressCountry: 'ES'
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO: JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      {/* Hero Section - Exact layout/typography per spec */}
      <section id="home" className="hero-surface hero-wrapper grid place-content-center w-full px-8 pt-0 pb-6"> {/* the 5 is the padding-top */}
        <div className="hero-scale-outer">
          <div className="hero-scale">
            <div className="max-w-5xl mx-auto w-full text-center">
              {/* Three headline lines */}
              <h1 className="line" style={{ fontFeatureSettings: '"ss01","ss05"' }}>
                <span id="t1">I'm Jon,</span>
                <span className="pic" aria-hidden="true">
                  <img src="/portada/jon-ortega.png" alt="Portrait of Jon Ortega" />
                </span>
              </h1>
              <h1 className="line" style={{ fontFeatureSettings: '"ss01","ss05"' }}>
                <span id="t2-1">a Data &amp; AI</span>
                <span className="pic" aria-hidden="true">
                  <img src="/portada/human-ai.webp" alt="Human and AI collaboration" />
                </span>
                <span id="t2-2">engineer</span>
              </h1>
              <h1 className="line" style={{ fontFeatureSettings: '"ss01","ss05"' }}>
                <span id="t3">{portfolioData.personal.location}</span>
                <span className="pic" aria-hidden="true">
                  <img src="/portada/mission.png" alt="Mission icon" />
                </span>
              </h1>

              {/* Description */}
              <div className="hero-desc mx-auto">
                {(() => {
                  const subtitle = portfolioData.personal.subtitle || '';
                  const idx = subtitle.toLowerCase().indexOf(' that ');
                  const before = idx > -1 ? subtitle.slice(0, idx + 5) : subtitle;
                  const after = idx > -1 ? subtitle.slice(idx + 5) : null;
                  return (
                    <p className="hero-subtitle">
                      {before}
                      {after && <span className="block sm:inline"> {after}</span>}
                    </p>
                  );
                })()}
                <div className="desc-more">
                  {portfolioData.personal.bio.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Fellowships Carousel */}
              <div className="mt-12">
                <h3 className="text-sm font-light text-gray-400 mb-3 uppercase tracking-widest">Part of</h3>
                <div className="overflow-hidden -mx-8 sm:mx-0">
                  <div className="flex gap-6 animate-scroll">
                    {/* Double the fellowships array for seamless loop */}
                    {[...portfolioData.fellowships, ...portfolioData.fellowships].map((fellowship, index) => {
                      const content = (
                        <div className="flex-shrink-0 h-20 w-[270px] bg-white rounded-lg flex items-center gap-0 px-3 py-0 shadow-sm border hover:shadow-md transition-shadow">
                          <img
                            src={fellowship.logo}
                            alt={fellowship.name}
                            className="h-14 w-auto object-contain flex-shrink-0"
                            onError={(e) => {
                              // Fallback to text if image fails to load
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="flex-1 text-center px-2">
                            <div className="text-[13px] sm:text-sm leading-tight text-gray-800 font-medium whitespace-normal break-words">{fellowship.name}</div>
                            {fellowship.description && (
                              <div className="text-[11px] sm:text-xs text-gray-500 leading-tight mt-0.5 whitespace-normal break-words">{fellowship.description}</div>
                            )}
                          </div>
                        </div>
                      );
                      return (
                        <div key={index} className="flex-shrink-0">
                          {fellowship.url ? (
                            <a
                              href={fellowship.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={fellowship.name}
                            >
                              {content}
                            </a>
                          ) : (
                            content
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Me Section */}


      {/* Wrappers AI - Enhanced Visual Appeal */}
      <section id="wrappers" className="pt-8 pb-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-gray-100 rounded-3xl -z-10"></div>

            <div className="p-6 lg:p-8">
              <div className="text-center mb-12">
                <div className="inline-block mb-4">
                  <Badge variant="outline" className="bg-white/80 backdrop-blur">
                    🚀 Featured Project
                  </Badge>
                </div>
                <h2 className="text-5xl md:text-6xl font-light tracking-tight mb-6">
                  Wrappers AI
                </h2>
                <p className="text-2xl text-gray-600 mb-4">Co-founder & CEO</p>
                <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto rounded-full"></div>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-6 lg:ml-8">
                  <p className="text-xl leading-relaxed text-gray-700">
                    {portfolioData.wrappersAI.description}
                  </p>

                  <div className="space-y-4">
                    {portfolioData.wrappersAI.responsibilities.map((responsibility, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mt-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-lg">{responsibility}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    size="lg"
                    className="mt-6 mx-auto sm:mx-0"
                    onClick={() => window.open('https://wrappers.es', '_blank')}
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Try Wrappers AI
                  </Button>
                </div>

                <div className="relative flex flex-col lg:flex-row lg:justify-end">
                  {/* Placeholder for product screenshot/demo */}
                  <div className="w-full sm:max-w-md ml-auto bg-transparent sm:bg-white rounded-2xl sm:shadow-2xl sm:p-4 sm:border">
                    <div className="bg-transparent sm:bg-gray-100 rounded-none sm:rounded-xl h-64 sm:h-80 flex items-center justify-center overflow-hidden">
                      <img
                        src={process.env.PUBLIC_URL + '/wrappers.png'}
                        alt="Wrappers AI"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.alt = 'Wrappers AI (preview unavailable)';
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                  {/* Floating tech badges */}
                  <div className="absolute -right-4 -top-4 space-y-2 hidden sm:block">
                    {portfolioData.wrappersAI.techStack.map((tech, index) => (
                      <Badge key={index} variant="secondary" className={`bg-white shadow-md ${tech === 'CrewAI' ? 'hidden sm:inline-flex' : ''}`}>
                        {tech}
                      </Badge>
                    ))}
                  </div>
                  {/* Mobile tech badges below image */}
                  <div className="mt-1 sm:hidden grid grid-cols-4 gap-1 place-items-center">
                    {portfolioData.wrappersAI.techStack.filter(t => t !== 'CrewAI').map((tech, index) => (
                      <Badge key={index} variant="secondary" className="bg-white shadow-sm text-[10px] px-2 py-[2px] rounded-full">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects - 4 Cards with image header and typed links */}
      <section id="projects" className="pt-8 pb-24 px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-16 text-center">Projects</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {portfolioData.sideProjects.slice(0, 4).map((project, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                {/* Hero image */}
                {project.photo && (() => {
                  const url = project.photo || '';
                  const needsPadding = url.includes('lookiero') || url.includes('brain');
                  const fitClass = needsPadding ? 'object-contain' : 'object-cover';
                  const padClass = needsPadding ? 'p-1' : '';
                  return (
                    <img src={url} alt={project.title} className={`h-40 w-full ${fitClass} ${padClass}`} />
                  );
                })()}
                <CardContent className={`p-6 ${project.photo ? 'pt-8' : ''}`}>
                  {/* Title */}
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-emerald-600 transition-colors">
                    {project.title}
                  </h3>
                  {/* Description */}
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {project.description}
                  </p>
                  {/* Tech badges */}
                  <div className="flex flex-wrap gap-1 mb-5">
                    {project.tech?.map((tech, techIndex) => (
                      <Badge key={techIndex} variant="secondary" className="text-xs">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                  {/* Typed links */}
                  <div className="flex flex-wrap gap-2">
                    {project.links?.map((link, i) => {
                      const t = (link.type || '').toLowerCase();
                      let color = 'bg-gray-100 text-gray-800 hover:bg-gray-200';
                      let Icon = LinkIcon;
                      let label = link.label || link.type || 'Link';
                      if (t.includes('github')) { color = 'bg-black text-white hover:bg-gray-900'; Icon = Code2; if (!link.label) label = 'GitHub'; }
                      else if (t.includes('medium')) { color = 'bg-emerald-600 text-white hover:bg-emerald-700'; Icon = BookOpen; if (!link.label) label = 'Medium'; }
                      else if (t.includes('pdf')) { color = 'bg-red-600 text-white hover:bg-red-700'; Icon = FileText; if (!link.label) label = 'PDF'; }
                      else if (t.includes('ipynb')) { color = 'bg-amber-500 text-white hover:bg-amber-600'; Icon = FileCode; if (!link.label) label = 'Notebook'; }
                      else if (t.includes('png') || t.includes('image') || t.includes('jpg')) { color = 'bg-sky-500 text-white hover:bg-sky-600'; Icon = ImageIcon; if (!link.label) label = 'Architecture'; }
                      return (
                        <Button
                          key={i}
                          size="sm"
                          className={`${color}`}
                          onClick={() => {
                            if (t.includes('pdf')) {
                              const isLocal = !/^https?:\/\//.test(link.url);
                              if (isLocal) {
                                setPreview({ type: 'pdf', url: resolveAssetUrl(link.url) });
                              } else {
                                window.open(resolveAssetUrl(link.url), '_blank');
                              }
                            } else if (t.includes('png') || t.includes('image') || t.includes('jpg')) {
                              setPreview({ type: 'image', url: resolveAssetUrl(link.url) });
                            } else {
                              window.open(link.url, '_blank');
                            }
                          }}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experience - Three Panel Design */}
      <section id="experience" className="pt-8 pb-24 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-16 text-center">Previous work experience</h2>

          <div className="exp-grid">
            {portfolioData.workExperience.map((job, index) => {
              const logoUrl = getExpLogoUrl(job.company);
              return (
                <div key={index} className="exp-card">
                  <div className="exp-banner" style={getExpBannerStyle(job.company)}>
                    <div className="exp-banner-color" style={getExpBannerLayerStyle(job.company)}></div>
                    <div className="exp-banner-tint"></div>
                    <div className="exp-company" style={getExpCompanyStyle(job.company)}>{job.company}</div>
                  </div>
                  <div className="exp-logo">
                    {logoUrl ? (
                      <img src={logoUrl} alt={job.company} className="exp-logo-img" />
                    ) : (
                      job.company?.split(' ').slice(0, 2).map(w => w[0]).join('')
                    )}
                  </div>
                  <div className="exp-body">
                    <h3 className="exp-role">{Array.isArray(job.roles) ? job.roles.join(' → ') : job.role}</h3>
                    <div className="exp-date">{job.duration}</div>
                    <p className="exp-desc">{job.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section id="tech" className="pt-8 pb-24 px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-12 text-center">Technologies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(portfolioData.technologies).map(([category, techs]) => {
              const IconComponent = techCategoryIcons[category] || Code2;
              const categoryNames = {
                aiMl: 'AI & ML',
                dataScience: 'Data Science',
                backend: 'Backend',
                frontend: 'Frontend',
                cloud: 'Cloud & DevOps',
                tools: 'Tools'
              };

              return (
                <Card key={category} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-4 h-4 text-gray-600" />
                      </div>
                      <h3 className="font-medium">{categoryNames[category]}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {techs.map((tech, index) => {
                        const iconUrl = getTechIconUrl(tech);
                        return (
                          <div key={index} className="flex items-center gap-1 bg-gray-50 px-2.5 py-1 rounded-full">
                            {iconUrl ? (
                              <img src={iconUrl} alt={tech} className="w-4 h-4 object-contain" />
                            ) : (
                              <div className="w-4 h-4 bg-gray-300 rounded-sm" />
                            )}
                            <span className="text-xs text-gray-700">{tech}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div
            className={`w-[96vw] max-w-[96vw] sm:max-w-[70vw] bg-white rounded-xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 border-b flex justify-between items-center shrink-0">
              <span className="text-sm text-gray-600">Preview</span>
              <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setPreview(null)}>Close</button>
            </div>
            <div className="flex-1 overflow-auto p-0">
              {preview.type === 'image' ? (
                <div className="w-full h-full grid place-items-center p-2">
                  <img src={preview.url} alt="preview" className="max-w-full max-h-full w-full h-auto object-contain" />
                </div>
              ) : (
                <div className="w-full h-full flex flex-col">
                  <iframe src={`${preview.url}#view=FitH`} title="PDF preview" className="w-full h-[70vh] sm:h-[78vh]" />
                  <div className="p-3 bg-gray-50 text-right shrink-0">
                    <a href={preview.url} download className="text-emerald-600 hover:text-emerald-700 text-sm">Download PDF</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Posts */}
      <section id="posts" className="pt-8 pb-24 px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-16 text-center">Posts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioData.blogPosts.map((post, index) => (
              <a
                key={index}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
              >
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2 cursor-pointer overflow-hidden">
                  {/* Featured Image */}
                  {post.image ? (
                    <img src={post.image} alt={post.title} className="h-48 w-full object-cover" />
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-gray-400" />
                    </div>
                  )}

                  <CardContent className="p-6">
                    {/* Platform Logo */}
                    <div className="flex items-center gap-2 mb-3">
                      {post.platform === 'YouTube' ? (
                        <img src="/posts/youtube.png" alt="YouTube" className="w-6 h-6 object-contain" />
                      ) : (
                        <img src="/posts/medium.png" alt="Medium" className="w-6 h-6 object-contain" />
                      )}
                      <span className="text-sm text-gray-500">{post.platform}</span>
                      <span className="text-sm text-gray-400">•</span>
                      <span className="text-sm text-gray-500">{post.date}</span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-emerald-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-6 line-clamp-3">
                      {post.description}
                    </p>

                    {/* Read More (visual only) */}
                    <div className="flex items-center text-emerald-600 text-sm group-hover:text-emerald-700">
                      <span>Read more</span>
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Mindset Section */}
      <AboutMe />

      {/* Contact */}
      <section id="contact" className="pt-16 pb-24 px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-12">Let's Connect</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Want to discuss a project or just say hi? My inbox is always open!
          </p>

          {/* Email Text */}
          <div className="text-xl text-gray-700 font-medium mb-8">
            jonortega20@gmail.com
          </div>

          <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
            {portfolioData.socialLinks.map((link, index) => {
              const isX = link.name.toLowerCase().includes('x');
              const IconComponent = iconMap[link.icon];
              return (
                <Button
                  key={index}
                  variant="outline"
                  size="lg"
                  className="flex items-center justify-center w-12 h-12 p-0 hover:shadow-lg transition-all hover:-translate-y-1 bg-white rounded-full"
                  onClick={() => window.open(link.url, '_blank')}
                >
                  {isX ? (
                    <img src={process.env.PUBLIC_URL + '/icons/x.svg'} alt="X" className="w-5 h-5" />
                  ) : (
                    <IconComponent className="w-5 h-5" />
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-8 border-t border-gray-100 bg-white mb-3 sm:mb-0">
        <div className="max-w-4xl mx-auto text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Jon Ortega<br /> Building the future with AI, one project at a time.</p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;